import type { TreeChildEdge, TreeNodeDefinition, TreeParams, TreePath } from '../../tree/tree/tree.model';
import type { UrnBallType } from './urn.model';

export interface UrnDrawColumn {
  readonly position: number;
  readonly label: string;
  readonly availableTypes: UrnBallType[];
  readonly selectedTypeId: string | null;
  readonly disabledTypeIds: Set<string>;
}

export interface UrnTreeResult {
  readonly tree: TreeParams;
  readonly selectedPath: TreePath;
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

/**
 * Compute UI columns for each draw position.
 * For each draw position, determines remaining ball counts after prior draws
 * (when drawing without replacement) and marks which types are disabled.
 */
export function computeDrawColumns(
  drawCount: number,
  withReplacement: boolean,
  ballTypes: readonly UrnBallType[],
  ballCounts: Readonly<Record<string, number>>,
  selections: ReadonlyArray<string | null>,
): UrnDrawColumn[] {
  // When without replacement, ballCounts may already be decremented.
  // Reconstruct original counts so draw columns reflect the correct state.
  const originalCounts = { ...ballCounts };
  if (!withReplacement) {
    for (const sel of selections) {
      if (sel !== null && sel !== undefined) {
        originalCounts[sel] = (originalCounts[sel] ?? 0) + 1;
      }
    }
  }

  return Array.from({ length: drawCount }, (_, i) => {
    // Compute remaining counts at this draw position
    const remaining = { ...originalCounts };

    if (!withReplacement) {
      for (let j = 0; j < i; j++) {
        const sel = selections[j];
        if (sel !== null && sel !== undefined && remaining[sel] > 0) {
          remaining[sel]--;
        }
      }
    }

    const disabledTypeIds = new Set<string>();
    for (const bt of ballTypes) {
      if ((remaining[bt.id] ?? 0) <= 0) {
        disabledTypeIds.add(bt.id);
      }
    }

    return {
      position: i,
      label: `${ordinal(i + 1)} draw`,
      availableTypes: [...ballTypes],
      selectedTypeId: selections[i] ?? null,
      disabledTypeIds,
    };
  });
}

/**
 * Build a TreeParams + selectedPath for `<dojo-tree>`.
 *
 * - Root node branches into one child per ball type with count > 0.
 * - Edge labels are probability fractions: `count/total`.
 * - Node content carries the ball type's label and color.
 * - Progressive expansion: only the selected branch at each depth expands.
 * - With replacement: same counts at every level.
 * - Without replacement: decrement selected type count, updating fractions.
 */
export function buildUrnTree(
  drawCount: number,
  withReplacement: boolean,
  ballTypes: readonly UrnBallType[],
  ballCounts: Readonly<Record<string, number>>,
  selections: ReadonlyArray<string | null>,
  options?: { expandAll?: boolean; zoomEnabled?: boolean },
): UrnTreeResult {
  const selectedPath: number[] = [];
  const expandAll = options?.expandAll ?? false;

  // When without replacement, ballCounts has already been decremented by drawn balls.
  // Reconstruct original counts so the tree always starts from the pre-draw state.
  const originalCounts = { ...ballCounts };
  if (!withReplacement) {
    for (const sel of selections) {
      if (sel !== null && sel !== undefined) {
        originalCounts[sel] = (originalCounts[sel] ?? 0) + 1;
      }
    }
  }

  function buildLevel(depth: number, currentCounts: Record<string, number>): TreeChildEdge[] | undefined {
    if (depth >= drawCount) {
      return undefined;
    }

    const total = Object.values(currentCounts).reduce((sum, n) => sum + n, 0);
    if (total === 0) {
      return undefined;
    }

    const available = ballTypes.filter((bt) => (currentCounts[bt.id] ?? 0) > 0);
    if (available.length === 0) {
      return undefined;
    }

    const selected = selections[depth];

    if (selected !== null && selected !== undefined) {
      const childIndex = available.findIndex((bt) => bt.id === selected);
      if (childIndex >= 0) {
        selectedPath.push(childIndex);
      }
    }

    return available.map((bt) => {
      const count = currentCounts[bt.id] ?? 0;
      const isSelected = bt.id === selected;

      let children: TreeChildEdge[] | undefined;
      if (expandAll || isSelected) {
        const nextCounts = { ...currentCounts };
        if (!withReplacement) {
          nextCounts[bt.id] = Math.max(0, nextCounts[bt.id] - 1);
        }
        children = buildLevel(depth + 1, nextCounts);
      }

      const node: TreeNodeDefinition = {
        id: `draw${depth}-${bt.id}`,
        content: { label: bt.label, color: bt.color, pattern: bt.pattern },
        children,
      };

      return { label: `${count}/${total}`, node };
    });
  }

  const root: TreeNodeDefinition = {
    id: 'root',
    children: buildLevel(0, { ...originalCounts }),
  };

  return {
    tree: {
      root,
      display: {
        nodeRadius: 8,
        showLeafEdgeLabels: true,
        ...(options?.zoomEnabled !== null && options?.zoomEnabled !== undefined
          ? { zoomEnabled: options.zoomEnabled }
          : {}),
      },
    },
    selectedPath,
  };
}
